import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import WebFont from 'webfontloader';
import { map, shareReplay } from 'rxjs/operators';
import { FontWeight } from '../../../../elements/resource/types/shape.type';

export interface Font {
  name: string;
  category: string;
  styles?: string[];
  weights?: string[];
}

export interface FontDescriptor {
  name: string;
  designer: string;
  license: string;
  category: string;
  date_added: string;

  fonts: [
    {
      name: string;
      style: string;
      weight: FontWeight;
      filename: string;
      post_script_name: string;
      full_name: string;
      copyright: string;
    },
  ];
}

@Injectable({
  providedIn: 'root',
})
export class FontService {
  private fontsUrl = 'assets/google_fonts.json'; // Path to the JSON file
  fonts$: Observable<Font[]>;
  fonts: Record<string, FontDescriptor>;

  constructor(private http: HttpClient) {
    this.fonts$ = this.http.get<Font[]>(this.fontsUrl).pipe(
      map(fonts => fonts.sort((f1, f2) => (f1.name > f2.name ? 1 : -1))),
      shareReplay(1),
    );

    this.fonts$.subscribe(fonts => {
      // -- // -- // console.log('fonts', fonts); // -- // -- //
      if (fonts) {
        this._loadFont('Goldman');
      }
    });
  }
  descriptors: Record<string, FontDescriptor> = {};

  loadFontDescriptorPromises: Record<string, Promise<FontDescriptor>> = {};

  async loadFontDescriptor(family) {
    if (this.loadFontDescriptorPromises[family]) {
      return this.loadFontDescriptorPromises[family];
    }

    const promise = this.http
      .get<FontDescriptor>(
        `assets/fonts/${family
          .split(' ')
          .map(word => word.toLowerCase())
          .join('_')}.json`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .toPromise();

    this.loadFontDescriptorPromises[family] = promise;

    return promise;
  }

  async waitForFontLoad(family: string) {
    // -- // -- // -- // -- //
  }

  loadFontPromises: Record<string, Promise<boolean>> = {};

  async loadFontFamily(family: string, fontDescriptor: FontDescriptor) {
    if (this.loadFontPromises[family]) {
      return this.loadFontPromises[family];
    }

    this.fontsToLoad[family] = true;

    const weights = fontDescriptor.fonts
      ?.map(({ weight, style }) =>
        style == 'italic' ? `${weight}italic` : weight,
      )
      .join(',');

    const _family =
      fontDescriptor.fonts?.length > 1 ? `${family}:${weights}` : family;

    WebFont?.load(
      {
        google: {
          families: [_family], // Specify weights 400 (regular) and 700 (bold)
        },
        active() {
          // console.log('active');
        },
        inactive(e) {
          // console.log('inactive', e);
        },
      },
      // () => {
      //   console.log('active');
      // },
      // e => {
      //   console.log('inactive', e);
      // },
    );

    let cnt = 0;
    const MAX_RETRIES = 30;
    const CHECK_INTERVAL_MS = 100;
    this.loadFontPromises[family] = new Promise<boolean>(resolve => {
      const interval = setInterval(() => {
        // Dirty hack for the tests
        const fontLoaded =
          !document.fonts || document.fonts.check(`12px '${family}'`);

        // -- //
        if (fontLoaded) {
          delete this.fontsToLoad[family];
          resolve(true);
          clearInterval(interval);
          return;
        }
        if (cnt == 15) {
          WebFont.load({
            google: {
              families: [_family], // Specify weights 400 (regular) and 700 (bold)
            },
          });
        }

        if (cnt == MAX_RETRIES) {
          resolve(true);
          clearInterval(interval);
        }
        cnt++;
      }, CHECK_INTERVAL_MS);
    });

    return this.loadFontPromises[family];
  }

  defaultFonts = [
    'Arial',
    'Verdana',
    'Tahoma',
    'Times New Roman',
    'Georgia',
    'Courier New',
    'Trebuchet MS',
    'Comic Sans MS',
    'sans-serif',
    'serif',
    'monospace',
  ];

  fontsToLoad: Record<string, boolean> = {};
  async _loadFont(family: string) {
    if (this.defaultFonts.includes(family)) {
      return;
    }

    if (this.fontsToLoad[family]) {
      return;
    }
    const fontDescriptor = await this.loadFontDescriptor(family);
    this.descriptors[family] = fontDescriptor;
    await this.loadFontFamily(family, fontDescriptor);
  }

  loadFont(font: Font, cb: () => void): void {
    if (this.defaultFonts.includes(font.name)) {
      return;
    }
    if (!font || !font.name) return;

    const weights = font.weights?.length ? font.weights.join(',') : '400';
    WebFont.load({
      google: {
        families: [`${font.name}:${weights}`], // Load specific weights
      },
      active: () => {
        console.log(`Font ${font.name} loaded successfully!`);
        cb?.();
      },
    });
  }
}
